import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { createRenderer } from '@/lib/universe/renderer'
import { subscribeFrame } from '@/lib/universe/store'

// A quiet stand-in for devices without WebGL: a black disc, a thin ring and a flattened glow
const FallbackHole: FC = (): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => subscribeFrame(frame => {
        const node = ref.current

        if (!node) return

        const size = Math.min(frame.width, frame.height) * Math.min(1.6, Math.max(0.05, 7 / frame.params.distance))

        node.style.width = `${size}px`
        node.style.height = `${size}px`
        node.style.transform = `translate3d(${frame.holeX}px, ${frame.holeY}px, 0) translate(-50%, -50%) rotate(${frame.params.roll}deg)`
        node.style.opacity = String(0.35 + 0.65 * frame.reveal)
        node.style.setProperty('--hole-tilt', String(Math.max(0.08, Math.sin(frame.params.inclination * Math.PI / 180))))
    }), [])

    return (
        <div
            ref={ref}
            aria-hidden='true'
            className='umbra-fallback-hole pointer-events-none fixed left-0 top-0 z-0'
        >
            <span />
        </div>
    )
}

const BlackHoleCanvas: FC = (): ReactNode => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        const canvas = canvasRef.current

        if (!canvas) return

        const renderer = createRenderer(canvas, () => setFailed(true))

        if (!renderer) {
            setFailed(true)

            return
        }

        const unsubscribe = subscribeFrame(frame => renderer.render(frame))

        return () => {
            unsubscribe()
            renderer.dispose()
        }
    }, [])

    return (
        <>
            <canvas
                ref={canvasRef}
                aria-hidden='true'
                className='pointer-events-none fixed inset-0 z-0 block h-full w-full bg-black'
            />

            {failed && <FallbackHole />}
        </>
    )
}

export default BlackHoleCanvas
